'use client';

import { useIsMobile, useIsTablet } from '@/components/hooks/use-mobile';
import Container from '@/components/layout/container/container';
import type { SectionBgVariant } from '@/lib/types/misc';
import { cn } from '@/lib/utils';
import { motion } from 'motion/react';
import { HTMLAttributes } from 'react';
import { Image } from '../image/image';
import { Link } from '../link/link';
import styles from './banner.module.scss';

type BannerProps = {
  heading?: string;
  subHeading?: string;
  cta?: {
    label?: string;
    url?: string;
    type?: 'Button' | 'Link';
    icon?: { src: string; alt?: string | null };
    withArrow?: boolean;
    newWindow?: boolean;
    // Form destination support - all TypeForm fields
    destination?: 'URL' | 'Page' | 'Form';
    ctaId?: string;
    formId?: number;
    formSlugId?: string;
    badge?: string;
    title?: string;
    description?: string;
    successTitle?: string;
    successDescription?: string;
    successCTA?: {
      label?: string;
      url?: string;
      type?: 'Button' | 'Link';
    };
  };
  backgroundVariant?: SectionBgVariant;
  backgroundImage?: string;
  isDarkBackground?: boolean;
  iconsSet1?: {
    src: string;
    alt: string;
  }[];
  iconsSet2?: {
    src: string;
    alt: string;
  }[];
  overlayIconsEnabled?: boolean;
} & HTMLAttributes<HTMLDivElement>;

export const Banner = ({
  heading,
  subHeading,
  cta,
  backgroundVariant = 'grey',
  backgroundImage,
  isDarkBackground = false,
  className,
  iconsSet1,
  iconsSet2,
  overlayIconsEnabled,
  ...props
}: BannerProps) => {
  const isTablet = useIsTablet();
  const isMobile = useIsMobile();
  const motionDelay = 0.2;
  let iconSize = 64;
  if (isTablet) {
    iconSize = 54;
  }
  if (isMobile) {
    iconSize = 58;
  }
  // Determine the CSS class for background variant
  const getBackgroundClass = () => {
    if (backgroundVariant === 'image' && !backgroundImage) {
      return styles.component_container_background_grey; // Fallback to grey
    }
    return styles[`component_container_background_${backgroundVariant}`];
  };

  return (
    <Container>
      <motion.div
        initial={overlayIconsEnabled ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={overlayIconsEnabled ? undefined : { duration: 0.6, delay: motionDelay }}
        viewport={{ once: true, amount: 0.3 }}
        className={cn(
          styles.motion_container,
          overlayIconsEnabled && styles.motion_container_overlay_icons,
        )}
      >
        <section
          className={cn(
            styles.component,
            getBackgroundClass(),
            isDarkBackground && styles['component--dark'],
            overlayIconsEnabled && styles.component_overlay_icons,
            className,
          )}
          {...props}
        >
          {backgroundVariant === 'image' && backgroundImage && (
            <div className={styles.component__bg_container}>
              <Image
                src={backgroundImage}
                alt="banner background"
                className={styles.component__bg_container__image}
                fill
              />
            </div>
          )}
          {overlayIconsEnabled && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', duration: 1, bounce: 0.5, delay: motionDelay + 0.2 }}
              viewport={{ once: true, amount: 0.3 }}
              style={{ width: '100%', position: 'absolute' }}
              className={styles.motion_large_circle}
            >
              <div className={styles.large_circle} />
            </motion.div>
          )}
          {overlayIconsEnabled && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', duration: 1, bounce: 0.5, delay: motionDelay + 0.2 }}
              viewport={{ once: true, amount: 0.3 }}
              style={{ width: '100%', position: 'absolute' }}
              className={styles.motion_small_circle}
            >
              <div className={styles.small_circle} />
            </motion.div>
          )}
          {overlayIconsEnabled && iconsSet1 && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0, x: 65, y: 10 }}
              whileInView={{ scale: 1, opacity: 1, x: 0, y: 0 }}
              transition={{ type: 'spring', duration: 1, bounce: 0.5, delay: motionDelay + 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <div className={styles.icons_set_1}>
                {iconsSet1.map((icon) => (
                  <div key={icon.src} className={styles.icons_set_1_icon}>
                    {/* <Image src={icon.src} alt={icon.alt} width={iconSize} height={iconSize} /> */}
                    <img src={icon.src} alt={icon.alt} width={iconSize} height={iconSize} />
                  </div>
                ))}
              </div>
            </motion.div>
          )}
          <div className={styles.component_container}>
            <div
              className={cn(
                styles.component_content,
                overlayIconsEnabled && styles.component_content_iconsEnabled,
              )}
            >
              <h3
                className={cn(
                  styles.component_heading,
                  overlayIconsEnabled && styles.component_heading_iconsEnabled,
                )}
              >
                {heading}
              </h3>
              <span className={styles.component_sub_heading}>{subHeading}</span>
            </div>
            {cta && (
              <Link
                label={cta.label || ''}
                url={cta.url}
                type={cta.type || 'Button'}
                icon={cta.icon}
                withArrow={cta.withArrow ?? true}
                target={cta.newWindow ? '_blank' : undefined}
                rel={cta.newWindow ? 'noopener noreferrer' : undefined}
                destination={cta.destination}
                ctaId={cta.ctaId}
                formId={cta.formId}
                formSlugId={cta.formSlugId}
                badge={cta.badge}
                title={cta.title}
                description={cta.description}
                successTitle={cta.successTitle}
                successDescription={cta.successDescription}
                successCTA={cta.successCTA}
                className={cn(
                  styles.component_cta,
                  overlayIconsEnabled && styles.component_cta_iconsEnabled,
                  cta.type === 'Link' && styles.component_cta_url,
                )}
              />
            )}
          </div>
          {overlayIconsEnabled && iconsSet2 && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0, x: -65, y: 10 }}
              whileInView={{ scale: 1, opacity: 1, x: 0, y: 0 }}
              transition={{ type: 'spring', duration: 1, bounce: 0.5, delay: motionDelay + 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <div className={styles.icons_set_2}>
                {iconsSet2.map((icon) => (
                  <div key={icon.src} className={styles.icons_set_2_icon}>
                    {/* <Image src={icon.src} alt={icon.alt} width={iconSize} height={iconSize} /> */}
                    <img src={icon.src} alt={icon.alt} width={iconSize} height={iconSize} />
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </section>
      </motion.div>
    </Container>
  );
};
